import { readonly, ref } from 'vue'

/**
 * « Réduire les animations » (Réglages › Accessibilité › Mouvement), en
 * **réactif**.
 *
 * Le CSS honorait déjà `prefers-reduced-motion` partout (`global.css` et les
 * blocs dédiés des composants). Ce qui manquait, c'est le pendant JS : deux
 * composants lisaient `matchMedia(...).matches` **une fois au `setup`**, pour
 * les mouvements qu'aucune règle CSS ne peut couper — l'auto-défilement du
 * module « À l'affiche », sa parallaxe et son étirement, le basculement des
 * vignettes de `LineupGrid`. Basculer le réglage app ouverte n'avait donc
 * d'effet qu'au prochain montage du composant (limite relevée à l'étape 1.5
 * du plan Apple, corrigée ici).
 *
 * Singleton de module, comme les autres composables de `src/composables/` :
 * une seule `MediaQueryList`, un seul écouteur, quel que soit le nombre de
 * composants qui la lisent. Rien à désabonner — la requête vit aussi longtemps
 * que l'app.
 *
 * Hors navigateur (ou sans `matchMedia` : vieilles webviews), on rend `false`,
 * c'est-à-dire « mouvement autorisé » — le comportement d'avant.
 */
const QUERY = '(prefers-reduced-motion: reduce)'

const reduced = ref(false)

if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const media = window.matchMedia(QUERY)
  reduced.value = media.matches

  const onChange = (event: MediaQueryListEvent) => {
    reduced.value = event.matches
  }

  // `addEventListener` depuis Safari 14 ; `addListener` est le repli des
  // webviews plus anciennes, où il reste la seule forme disponible
  if (typeof media.addEventListener === 'function') media.addEventListener('change', onChange)
  else if (typeof media.addListener === 'function') media.addListener(onChange)
}

export function useReducedMotion() {
  return readonly(reduced)
}
